import { api } from "@config/axios/axios.config";
import { SERVER_ROUTES } from "@constants/api.routes";
import { generateUploadPayload, type FileUploadConfig } from "./fileUploadUtils";

export interface UploadUrlResponse {
	statusCode: number;
	message: string;
	data: {
		uploadUrl: string;
		objectKey: string;
	};
}

export interface AttachmentUploadResponse {
	statusCode: number;
	message: string;
	data: {
		url: string;
		objectKey: string;
	};
}

export const uploadAttachment = async (
	file: File,
	config: FileUploadConfig,
	onProgress?: (progress: number) => void,
): Promise<string> => {
	try {
		// 1. Generate the new payload format
		const uploadPayload = generateUploadPayload(file, config);

		// 2. Get presigned URL and objectKey with new payload
		const uploadUrlResponse = await api.post<UploadUrlResponse | { uploadUrl: string; objectKey: string }>(
			SERVER_ROUTES.INSTITUTE.ATTACHMENT_UPLOAD,
			uploadPayload,
		);

		// Handle different response structures
		// The axios interceptor returns response.data directly, so we need to check both structures
		let uploadUrl: string;
		let objectKey: string;

		// @ts-expect-error - Handle different response structures from API
		const response: { data?: { uploadUrl?: string; objectKey?: string }; uploadUrl?: string; objectKey?: string } = uploadUrlResponse;

		// Check if response has nested data structure (UploadUrlResponse type)
		if (response?.data?.uploadUrl && response?.data?.objectKey) {
			uploadUrl = response.data.uploadUrl;
			objectKey = response.data.objectKey;
		} else if (response?.uploadUrl && response?.objectKey) {
			// Direct response structure (as returned by the API)
			uploadUrl = response.uploadUrl;
			objectKey = response.objectKey;
		} else {
			console.error("Upload URL response structure:", response);
			throw new Error("Failed to get upload URL from response");
		}

		console.log("Got upload URL and objectKey:", { uploadUrl, objectKey });

		// Step 3: Upload file to MinIO with progress tracking and timeout
		return new Promise<string>((resolve, reject) => {
			const xhr = new XMLHttpRequest();
			const timeout = 15 * 60 * 1000; // 15 minutes timeout for large files

			// Set up timeout
			const timeoutId = setTimeout(() => {
				xhr.abort();
				reject(new Error("Upload timeout. Please try again with a smaller file or check your internet connection."));
			}, timeout);

			// Track upload progress
			xhr.upload.addEventListener("progress", event => {
				if (event.lengthComputable && onProgress) {
					const progress = Math.round((event.loaded / event.total) * 100);
					onProgress(progress);
				}
			});

			// Handle completion
			xhr.addEventListener("load", () => {
				clearTimeout(timeoutId);
				if (xhr.status >= 200 && xhr.status < 300) {
					resolve(objectKey);
				} else {
					const errorText = xhr.responseText || `HTTP ${xhr.status}`;
					console.error("MinIO upload failed:", errorText);
					reject(new Error(`Failed to upload file: ${errorText}`));
				}
			});

			// Handle errors
			xhr.addEventListener("error", () => {
				clearTimeout(timeoutId);
				reject(new Error("Network error during upload. Please check your connection and try again."));
			});

			xhr.addEventListener("abort", () => {
				clearTimeout(timeoutId);
				reject(new Error("Upload was cancelled or timed out."));
			});

			// Start upload
			xhr.open("PUT", uploadUrl);
			xhr.setRequestHeader("Content-Type", file.type);
			xhr.send(file);
		});
	} catch (error) {
		console.error("Attachment upload failed:", error);
		if (error instanceof Error) {
			throw error;
		}
		throw new Error("Failed to upload attachment");
	}
};

